// src/auth/AcceptInvitationPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { StyledButton, AlertBox } from '../main/CommonStyles';
import styled from 'styled-components';
import apiClient from '../services/api';
import authService from '../services/authService'; // NOWY IMPORT
import { Loader2, AlertTriangle, UserPlus, ExternalLink } from 'lucide-react';

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const StyledInput = styled.input`
    padding: 0.75rem;
    border: 1px solid #ccc;
    border-radius: 8px;
`;

const OAuthSeparator = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    color: ${({ theme }) => theme.colors.textMedium};
    font-size: 0.85rem;
    margin: 1rem 0;

    &::before, &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    }
    &:not(:empty)::before { margin-right: .5em; }
    &:not(:empty)::after { margin-left: .5em; }
`;

const OAuthButton = styled(StyledButton)`
    width: 100%;
    justify-content: center;
`;

const AcceptInvitationPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
    });

    const validateToken = useCallback(async () => {
        try {
            const response = await apiClient.get(`/invitations/${token}`);
            setInvitation(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Nieprawidłowy lub wygasły link zaproszenia.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        validateToken();
    }, [validateToken]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            return setError('Hasła nie są zgodne.');
        }
        setLoading(true);
        setError('');
        try {
            await apiClient.post('/invitations/accept', { ...formData, token });
            navigate('/login', { state: { message: 'Konto zostało pomyślnie utworzone! Zaloguj się, aby kontynuować.' } });
        } catch (err) {
            setError(err.response?.data?.message || 'Wystąpił błąd podczas tworzenia konta.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <AuthLayout title="Weryfikacja Zaproszenia"><Loader2 size={32} className="icon-spin" /> Proszę czekać...</AuthLayout>;
    }

    return (
        <AuthLayout title="Witaj w ePsycholog!" subtitle="Dokończ proces rejestracji, aby dołączyć do naszej społeczności.">
            {error && <AlertBox type="error"><AlertTriangle/>{error}</AlertBox>}
            {invitation && (
                <>
                    <p>Tworzysz konto dla adresu e-mail: <strong>{invitation.email}</strong></p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
                        <OAuthButton as="a" href={authService.getOAuth2LoginUrlWithInvitation('google', token)} $provider="google">
                            <ExternalLink size={18} /> Kontynuuj z Google
                        </OAuthButton>
                        <OAuthButton as="a" href={authService.getOAuth2LoginUrlWithInvitation('facebook', token)} $provider="facebook">
                            <ExternalLink size={18} /> Kontynuuj z Facebook
                        </OAuthButton>
                    </div>

                    <OAuthSeparator>lub dokończ ręcznie</OAuthSeparator>

                    <StyledForm onSubmit={handleSubmit}>
                        <StyledInput name="firstName" placeholder="Imię" onChange={handleChange} required />
                        <StyledInput name="lastName" placeholder="Nazwisko" onChange={handleChange} required />
                        <StyledInput name="username" placeholder="Nazwa użytkownika" onChange={handleChange} required />
                        <StyledInput type="password" name="password" placeholder="Hasło (min. 6 znaków)" onChange={handleChange} required />
                        <StyledInput type="password" name="confirmPassword" placeholder="Potwierdź hasło" onChange={handleChange} required />
                        <StyledButton type="submit" disabled={loading}>
                            {loading ? <Loader2 className="icon-spin" /> : <UserPlus />}
                            {loading ? 'Tworzenie konta...' : 'Utwórz Konto'}
                        </StyledButton>
                    </StyledForm>
                </>
            )}
        </AuthLayout>
    );
};

export default AcceptInvitationPage;